import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  InputVariant,
  Notification,
  PassiveVariants,
  Select,
  SelectOption,
} from '@pluxee-design-system/core';
import { RadioGroupControl } from '@pluxee-design-system/react-hook-form';
import { warnEcoPassModalParams } from 'components/Products/modals';
import { isEcoPass } from 'components/Products/PayoutCard/utils';
import { payoutSchema } from 'config/schemas/products';
import useModal from 'context/ModalContext';
import useDeepMemo from 'data/useDeepMemo';
import { PayoutChannel, PayoutFrequency } from 'generated/models';
import useTranslations from 'i18n';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SingleValue } from 'react-select';
import isKeyEmpty from 'utils/isKeyEmpty';
import PayoutCard from '../PayoutCard';
import { PartnerAccountsDict, PayoutsFormValues } from '../types';
import { preselectTab } from './utils';

interface PayoutsStepProps {
  children?: ReactNode;
  isEditable: boolean;
  isSelectable: boolean;
  isSubmittable: boolean;
  isStepValid: boolean;
  onSelectPayout: (payout: PayoutChannel, accountNumber?: string, defaultAccount?: string) => void;
  onSubmit: (values: PayoutsFormValues) => void;
  onShowLocations: () => void;
  partnerAccounts: PartnerAccountsDict;
  payouts: Record<string, PayoutFrequency>;
  productNameKey?: string;
  selectedPayout?: PayoutChannel;
  showWarning?: boolean;
  withLocations: boolean;
  onTrackPayoutFrequency: (frequency: string) => void;
}

const PayoutsStep = ({
  children,
  isEditable,
  isSelectable,
  isSubmittable,
  isStepValid,
  onSelectPayout,
  onSubmit,
  partnerAccounts,
  payouts,
  productNameKey,
  selectedPayout,
  showWarning,
  // tracking
  onTrackPayoutFrequency,
}: PayoutsStepProps) => {
  const methods = useForm<PayoutsFormValues>({
    mode: 'onChange',
    defaultValues: useDeepMemo(
      () => ({
        payout: selectedPayout?.payoutUid ?? '',
        parameterCode: selectedPayout?.parameterCode ?? '', // helper
        partnerAccounts: {
          ...Object.values(payouts).reduce((result, { channelList }) => {
            channelList.forEach(({ parameterCode }) => {
              if (parameterCode) result[String(parameterCode)] = '';
            });
            return result;
          }, {} as PartnerAccountsDict),
          ...(selectedPayout?.parameterCode ? { [selectedPayout?.parameterCode]: '' } : {}),
          ...partnerAccounts,
        },
      }),
      [partnerAccounts, payouts, selectedPayout],
    ),
    resolver: useMemo(() => yupResolver(payoutSchema(isSelectable)), [isSelectable]),
  });
  const { formState, setValue, watch } = methods;
  const valuesPartnerAccounts = watch('partnerAccounts');

  const showModal = useModal();
  const { t, tHtml } = useTranslations();
  const tabs = useMemo<SelectOption[]>(
    () =>
      Object.keys(payouts)
        .filter((key) => payouts[key].channelList.length > 0)
        .sort((keyA, keyB) => (payouts[keyA].weight ?? 0) - (payouts[keyB].weight ?? 0))
        .map((tab) => ({
          value: tab,
          label: t(`product_details.payoutFrequency.${tab}`, payouts[tab]?.name),
        })),
    [payouts, t],
  );
  const [selectedTab, setSelectedTab] = useState(() =>
    preselectTab(tabs, payouts, selectedPayout?.payoutUid),
  );
  const handleSelectTab = useCallback(
    (tab: SingleValue<SelectOption>) => {
      if (tab) {
        onTrackPayoutFrequency?.(tab.label);
        setSelectedTab(tab);
      }
    },
    [onTrackPayoutFrequency, setSelectedTab],
  );
  const handleSelectPayout = useCallback(
    async (newValue: string) => {
      const newPayout = payouts[selectedTab?.value]?.channelList?.find(
        (l) => l.payoutUid === newValue,
      );
      if (newPayout) {
        const isEco = isEcoPass(newPayout);
        if ((isEco && (await showModal(warnEcoPassModalParams(t, tHtml)))) || !isEco) {
          onSelectPayout(newPayout, valuesPartnerAccounts[String(newPayout.parameterCode)]);
          setValue('payout', newValue, {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue('parameterCode', newPayout.parameterCode ?? '', {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    },
    [
      onSelectPayout,
      payouts,
      selectedTab?.value,
      setValue,
      showModal,
      valuesPartnerAccounts,
      t,
      tHtml,
    ],
  );

  useEffect(
    () => setSelectedTab(preselectTab(tabs, payouts, selectedPayout?.payoutUid)),
    [tabs, setSelectedTab, payouts, selectedPayout?.payoutUid],
  );

  return (
    <FormProvider {...methods}>
      <Flex gap={10} direction="column">
        <Text
          as="div"
          variant="subtitle.desktopMain"
          mt={2}
          color="semantic.text.tertiary"
          align="center"
        >
          {tHtml('product_details.products.*.product.descriptionInSidebar', '', {
            query: { where: 'product_details.products.*.product.code', is: productNameKey },
          })}
        </Text>
        <Box>
          <Text variant="title.desktopCard">
            {t('products.sidebarTexts.header', 'Select an offer')}
          </Text>
          <Text variant="subtitle.card" color="semantic.text.tertiary" mt={1} mb={4}>
            {t(
              'products.sidebarTexts.selectOfferDescription',
              'Choose the pricing option for your product',
            )}
          </Text>
          {showWarning && (
            <Notification
              description={t(
                'products.sidebarTexts.informativeOptionsNote',
                'The following options are only informative. The payout channel is defined by the mean of delivery of the vouchers and the payout channel you choose for each of the remittance.',
              )}
              isClosable={false}
              withBorder={false}
              withoutShadow
              variant={PassiveVariants.PASSIVE_INFORMATION}
            />
          )}
          {tabs.length > 0 && !isKeyEmpty(tabs[0]?.value) && (
            <Select
              mandatory
              name="frequency"
              id="ProductDrawer_frequency"
              placeholder={t('product_details.prices.payoutFrequency', 'Frequency')}
              title={t('product_details.prices.payoutFrequency', 'Frequency')}
              options={tabs}
              value={selectedTab}
              onChange={handleSelectTab}
              variant={tabs.length > 1 ? undefined : InputVariant.READONLY}
            />
          )}
          <RadioGroupControl
            name="payout"
            onChange={handleSelectPayout}
            stackProps={{ mt: 6, direction: 'column' }}
          >
            {payouts[selectedTab?.value]?.channelList?.map((channel) => (
              <PayoutCard
                key={channel.payoutUid}
                channel={channel}
                isDisabled={!isSelectable || !isEditable}
                isSelectable={isSelectable}
                isSelected={channel === selectedPayout}
                hasError={
                  Boolean(formState.errors.payout) ||
                  Boolean(formState.errors.partnerAccounts?.[String(channel.parameterCode)])
                }
                productNameKey={productNameKey}
              />
            ))}
          </RadioGroupControl>
        </Box>
      </Flex>
      <VStack
        position="sticky"
        spacing={6}
        bottom={0}
        background="semantic.surface.1"
        py={6}
        mb={-6}
      >
        {children}
        {isSubmittable && (
          <Button
            onClick={methods.handleSubmit(onSubmit)}
            isDisabled={!formState.isValid || !isStepValid}
            type="button"
            id="PayoutsStep_applyButton"
            variant="primaryFilled"
          >
            {t('products.sidebarButtons.apply', 'Apply')}
          </Button>
        )}
      </VStack>
    </FormProvider>
  );
};

export default PayoutsStep;
